/**
 * ResetPassword page for Wadakaroo
 *
 * Shown when a user logs in with a restricted PASSWORD_RESET_REQUIRED token
 * (e.g. first login after admin provisioning, admin-triggered reset, or any
 * other IMMEDIATE_RESET_REQUIRED reason returned by the backend).
 *
 * The restricted token grants access to exactly ONE endpoint — POST
 * /auth/password/reset. Everything else 403s until the reset completes.
 *
 * Flow:
 *   1. User submits current + new password
 *   2. Thunk calls /auth/password/reset with Bearer restricted-token
 *   3. On success, auth is cleared and user is bounced to /login with a
 *      success banner (handled in authSlice + Login page)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    resetPassword,
    logoutUser,
    clearError,
    selectUser,
    selectAuthError,
    selectIsLoading,
    selectPasswordResetMessage,
    selectPasswordResetReason,
} from '../store/slices/authSlice';

// Keep the rules simple and explicit — easier to align with backend validator
// than a regex soup. Mirror whatever PasswordPolicyService enforces server-side.
const PASSWORD_RULES = [
    { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
    { id: 'digit', label: 'One number', test: (p) => /\d/.test(p) },
];

const ResetPassword = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const user = useAppSelector(selectUser);
    const error = useAppSelector(selectAuthError);
    const isLoading = useAppSelector(selectIsLoading);
    const resetMessage = useAppSelector(selectPasswordResetMessage);
    const resetReason = useAppSelector(selectPasswordResetReason);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    const [localError, setLocalError] = useState(null);

    // Clear any lingering server error when the user starts editing
    useEffect(() => {
        if (error) dispatch(clearError());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPassword, newPassword, confirmPassword]);

    const ruleResults = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(newPassword) }));
    const allRulesPass = ruleResults.every((r) => r.ok);
    const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
    const isDifferentFromCurrent = newPassword.length === 0 || newPassword !== currentPassword;
    const canSubmit = currentPassword.length > 0
        && allRulesPass
        && passwordsMatch
        && isDifferentFromCurrent
        && !isLoading;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError(null);

        if (!allRulesPass) {
            setLocalError('New password does not meet the requirements below.');
            return;
        }
        if (!passwordsMatch) {
            setLocalError('New password and confirmation do not match.');
            return;
        }
        if (!isDifferentFromCurrent) {
            setLocalError('New password must be different from your current password.');
            return;
        }

        const result = await dispatch(resetPassword({ currentPassword, newPassword }));

        if (resetPassword.fulfilled.match(result)) {
            // authSlice has cleared auth and set passwordResetSuccess = true.
            // Login route will read that flag and show the success banner.
            navigate('/login', { replace: true });
        }
    };

    const handleCancel = async () => {
        // Abandon the reset — log out and bounce to login
        await dispatch(logoutUser());
        navigate('/login', { replace: true });
    };

    const displayMessage = resetMessage
        || (resetReason === 'IMMEDIATE_RESET_REQUIRED'
            ? 'For security, your password must be reset before continuing.'
            : 'Your password must be reset before continuing.');

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                    <div className="mb-6">
                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                            <svg
                                className="w-6 h-6 text-emerald-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 15v2m0 0v2m0-2h2m-2 0h-2m6-6V7a4 4 0 10-8 0v4M5 11h14a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2v-7a2 2 0 012-2z"
                                />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-display font-bold text-gray-900">
                            Reset your password
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">{displayMessage}</p>
                        {user?.email && (
                            <p className="mt-1 text-xs text-gray-500">
                                Signed in as <span className="font-medium">{user.email}</span>
                            </p>
                        )}
                    </div>

                    {(error || localError) && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                            {error || localError}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                        <div>
                            <label
                                htmlFor="currentPassword"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                Current password
                            </label>
                            <input
                                id="currentPassword"
                                type={showPasswords ? 'text' : 'password'}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                autoComplete="current-password"
                                autoFocus
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="newPassword"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                New password
                            </label>
                            <input
                                id="newPassword"
                                type={showPasswords ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="confirmPassword"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                Confirm new password
                            </label>
                            <input
                                id="confirmPassword"
                                type={showPasswords ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                                required
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm ${
                                    confirmPassword.length > 0 && !passwordsMatch
                                        ? 'border-red-300'
                                        : 'border-gray-300'
                                }`}
                            />
                            {confirmPassword.length > 0 && !passwordsMatch && (
                                <p className="mt-1 text-xs text-red-600">Passwords do not match.</p>
                            )}
                        </div>

                        <div className="flex items-center">
                            <input
                                id="showPasswords"
                                type="checkbox"
                                checked={showPasswords}
                                onChange={(e) => setShowPasswords(e.target.checked)}
                                className="h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                            />
                            <label
                                htmlFor="showPasswords"
                                className="ml-2 text-sm text-gray-600 cursor-pointer select-none"
                            >
                                Show passwords
                            </label>
                        </div>

                        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                                Password requirements
                            </p>
                            <ul className="space-y-1">
                                {ruleResults.map((r) => (
                                    <li
                                        key={r.id}
                                        className={`text-xs flex items-center ${
                                            r.ok ? 'text-emerald-700' : 'text-gray-500'
                                        }`}
                                    >
                                        <span className="mr-2">{r.ok ? '✓' : '○'}</span>
                                        {r.label}
                                    </li>
                                ))}
                                {newPassword.length > 0 && !isDifferentFromCurrent && (
                                    <li className="text-xs flex items-center text-red-600">
                                        <span className="mr-2">✕</span>
                                        Must differ from current password
                                    </li>
                                )}
                            </ul>
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={isLoading}
                                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm font-semibold disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={!canSubmit}
                                className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Resetting…' : 'Reset password'}
                            </button>
                        </div>
                    </form>
                </div>

                <p className="text-center text-xs text-gray-500 mt-4">
                    After resetting, you'll be asked to log in again with your new password.
                </p>
            </div>
        </div>
    );
};

export default ResetPassword;